/** PushPrompt — pide activar avisos en el momento útil (al ver su primera cita / en la agenda del barbero), nunca al abrir
 * la app. En iPhone sin la app instalada explica cómo instalarla para recibir avisos. Se puede posponer 7 días. */
import { useEffect, useState } from 'react';
import { BellRing, Share, PlusSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';
import { currentSubscription, enablePush, needsIosInstall, promptSnoozed, pushPermission, pushSupported, snoozePrompt } from '@/pwa/push';
import { pushApi } from '@/lib/pushApi';

export const PushPrompt = ({ reason = tr("Te avisamos al instante de cambios, recordatorios y pagos de tus citas.") }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const ios = needsIosInstall();

  useEffect(() => {
    if (promptSnoozed()) return;
    if (ios) { setShow(true); return; }
    if (!pushSupported() || pushPermission() === 'denied') return;
    // Permiso ya concedido pero sin suscripción en el servidor (apagado en Ajustes o podada tras 404/410): si el navegador
    // aún tiene suscripción se re-sincroniza en silencio; si no, se vuelve a ofrecer.
    pushApi.status().then(async (s) => {
      if (s?.subscriptions) return;
      if (pushPermission() === 'granted') {
        const sub = await currentSubscription().catch(() => null);
        if (sub) { await pushApi.subscribe(sub.toJSON()).catch(() => {}); return; }
      }
      setShow(true);
    }).catch(() => setShow(true));
  }, [ios]);

  if (!show) return null;
  const dismiss = () => { snoozePrompt(); setShow(false); };
  const enable = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r === 'granted') { toast.success(tr("Avisos activados en este dispositivo")); setShow(false); }
      else { toast.info(tr("Puedes activar los avisos cuando quieras desde Ajustes → Notificaciones")); dismiss(); }
    } catch { toast.error(tr("No se pudieron activar los avisos")); } finally { setBusy(false); }
  };

  return (
    <div className="mb-3 bg-primary/5 border border-primary/30 rounded-2xl p-3.5 relative" data-testid="push-prompt">
      <button type="button" onClick={dismiss} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground" aria-label={tr("Ahora no")} data-testid="push-prompt-dismiss"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <BellRing className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{ios ? tr("Instala Ziyup para recibir avisos") : tr("¿Quieres avisos de tus citas?")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{ios
            ? tr("En iPhone los avisos solo llegan con la app instalada en la pantalla de inicio.")
            : reason}</p>
          {ios ? (
            <ol className="mt-2 space-y-1 text-[11px] text-foreground/90" data-testid="push-prompt-ios-steps">
              <li className="inline-flex items-center gap-1.5"><Share className="w-3.5 h-3.5 text-primary" /> {tr("1. Toca «Compartir» en Safari")}</li><br />
              <li className="inline-flex items-center gap-1.5"><PlusSquare className="w-3.5 h-3.5 text-primary" /> {tr("2. «Agregar a pantalla de inicio» y abre Ziyup desde ahí")}</li>
            </ol>
          ) : (
            <div className="mt-2 flex gap-2">
              <button type="button" disabled={busy} onClick={enable} className="rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 hover:opacity-90 disabled:opacity-60" data-testid="push-prompt-enable">{tr("Activar avisos")}</button>
              <button type="button" onClick={dismiss} className="rounded-full border border-border text-xs font-semibold px-4 py-2 text-muted-foreground" data-testid="push-prompt-later">{tr("Ahora no")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
